/**
 * JustEasySEO — Contrast checker (M144 Fase 1).
 *
 * Verifieert dat elke gebruikte kleur-combinatie in de nieuwe design-tokens
 * WCAG-contrast haalt (4.5:1 gewone tekst / 3:1 grote tekst >= 18px bold
 * of >= 24px regular).
 *
 * Kan gedraaid worden vanuit CLI (`node src/theme/contrast.js`) én
 * geïmporteerd in `DesignSystemPage` om de output live te tonen.
 */
import { colors } from "./tokens";

function toRgb(hex) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h.length === 3
    ? h.split("").map((c) => c + c).join("") : h, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}
function relLum({ r, g, b }) {
  const conv = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * conv(r) + 0.7152 * conv(g) + 0.0722 * conv(b);
}
export function ratio(fg, bg) {
  const [l1, l2] = [relLum(toRgb(fg)), relLum(toRgb(bg))].sort((a, b) => b - a);
  return (l1 + 0.05) / (l2 + 0.05);
}
function round(n) { return Math.round(n * 100) / 100; }

// Combinaties die daadwerkelijk in de UI voorkomen (op basis van tokens.js).
const PAIRS = [
  // Body / primaire tekst op canvassen
  ["Primaire tekst", colors.textPrimary, "op warm wit", colors.bgCanvas, "normal"],
  ["Primaire tekst", colors.textPrimary, "op wit", colors.bgPanel, "normal"],
  ["Primaire tekst", colors.textPrimary, "op mint", colors.accentMint, "normal"],
  ["Secundaire tekst", colors.textMuted, "op warm wit", colors.bgCanvas, "normal"],
  ["Secundaire tekst", colors.textMuted, "op wit", colors.bgPanel, "normal"],
  ["Bijschrift", colors.textSubtle, "op warm wit", colors.bgCanvas, "large"],
  ["Bijschrift", colors.textSubtle, "op wit", colors.bgPanel, "large"],
  // Petrol als link / knop-vulling
  // Petrol-link op canvas — voor tekst-links op canvas gebruiken we
  // altijd de hover-tint (#066A75) van de merkgids i.p.v. petrol-fill,
  // omdat #087F8C op #F7F8F5 net onder de 4.5:1 grens ligt. Dit is een
  // bewust hanteerbare regel; op solid-petrol vulling gebruiken we
  // uiteraard nog steeds de merkgids-hex #087F8C.
  ["Petrol-link (hover-tint)", colors.accentPetrolHover, "op warm wit", colors.bgCanvas, "normal"],
  ["Petrol-link (hover-tint)", colors.accentPetrolHover, "op wit", colors.bgPanel, "normal"],
  ["Witte tekst", colors.textOnPetrol, "op petrol", colors.accentPetrol, "normal"],
  ["Witte tekst", colors.textOnPetrol, "op petrol-hover", colors.accentPetrolHover, "normal"],
  // Status
  ["Succes-tekst", colors.posGreen, "op wit", colors.bgPanel, "normal"],
  ["Waarschuwing-tekst", colors.warnAmber, "op wit", colors.bgPanel, "normal"],
  ["Fout-tekst", colors.negRed, "op wit", colors.bgPanel, "normal"],
  // Merk-accent — abrikoos NOOIT als kleine tekst op wit; wel als vulling
  ["Donker op abrikoos", colors.textOnApricot, "op abrikoos", colors.accentApricot, "normal"],
];

export function runContrastCheck() {
  return PAIRS.map(([label, fg, bgLabel, bg, size]) => {
    const r = round(ratio(fg, bg));
    const need = size === "large" ? 3.0 : 4.5;
    return {
      label, fg, bg, bgLabel, size, ratio: r, need,
      pass: r >= need,
    };
  });
}

// CLI: alleen wanneer dit script rechtstreeks gedraaid wordt.
if (typeof process !== "undefined" && process.argv &&
    process.argv[1] && process.argv[1].endsWith("contrast.js")) {
  const rows = runContrastCheck();
  const pad = (s, n) => (s + " ".repeat(n)).slice(0, n);
  console.log("JustEasySEO — contrast check");
  console.log("=".repeat(78));
  console.log(pad("Combinatie", 44), pad("Grootte", 8), pad("Ratio", 8),
              pad("Nodig", 8), "Status");
  console.log("-".repeat(78));
  rows.forEach(r => {
    console.log(
      pad(`${r.label} (${r.fg}) ${r.bgLabel}`, 44),
      pad(r.size, 8),
      pad(`${r.ratio}:1`, 8),
      pad(`${r.need}:1`, 8),
      r.pass ? "PASS" : "FAIL",
    );
  });
  const failing = rows.filter(r => !r.pass);
  console.log("=".repeat(78));
  console.log(`Totaal: ${rows.length} · Faal: ${failing.length}`);
  if (failing.length) process.exit(1);
}
